/* /ai — AI automation hub (prototype ai.js pgAI): features with enable switch + model (owner),
 * credits used/limit, time saved, try-it samples. */
import { useState } from "react";
import { useSession } from "../../app/session";
import { useOverlays } from "../../app/overlays";
import { useAiFeatures, useUpdateAiFeature } from "../../data/hooks";
import type { AiConfig, AiFeature } from "../../data/types";
import { useI18n } from "../../i18n";
import { Alert, Bar, Button, Icon, Kpi, Loaded, PageHeader, Select, Toggle, errMsg, useToast } from "../../ui";
import { AiTryModal } from "./AiTryModal";

export const timeSavedHours = (fs: AiFeature[]) => fs.reduce((a, f) => a + f.saveMinutes * f.uses, 0) / 60;

function FeatureCard({ f, cfg, canEdit, onTry }: { f: AiFeature; cfg: AiConfig; canEdit: boolean; onTry: () => void }) {
  const { L } = useI18n();
  const toast = useToast();
  const upd = useUpdateAiFeature();
  const models = Array.from(new Set([f.model, ...cfg.providers.filter((p) => p.connected).flatMap((p) => p.models).filter((m) => m !== "set-model-id")]));
  const save = async (patch: { enabled?: boolean; model?: string }, ok: string) => {
    try { await upd.mutateAsync({ id: f.id, patch }); toast(ok); } catch (e) { toast(errMsg(e), "bad"); }
  };
  return (
    <section className="card card-b stack" style={{ gap: 10 }} data-testid={"ai-feature-" + f.id}>
      <div className="row nw between">
        <span className="row nw"><span className="kpi-ico gold"><Icon name={f.icon} /></span><b>{L(f.name, f.nameAr)}</b></span>
        {canEdit
          ? <Toggle checked={f.enabled} label={L(f.name, f.nameAr)} testId={"ai-toggle-" + f.id} onChange={(v) => save({ enabled: v }, v ? L(f.name + " turned on", "تم تشغيل " + f.nameAr) : L(f.name + " turned off", "تم إيقاف " + f.nameAr))} />
          : <span className={"chip " + (f.enabled ? "ok" : "")} data-testid={"ai-state-" + f.id}>{f.enabled ? L("On", "مفعل") : L("Off", "متوقف")}</span>}
      </div>
      <p className="sm muted" style={{ margin: 0 }}>{L(f.description, f.descriptionAr)}</p>
      <div className="row xs muted"><Icon name="layers" className="s" />{f.where} · ~{f.saveMinutes}{L(" min saved each", " دقيقة لكل مرة")} · {f.uses}{L(" uses", " استخدام")}{f.pii && <span className="chip warn" style={{ marginInlineStart: 6 }}>{L("Personal data", "بيانات شخصية")}</span>}</div>
      <div className="row nw between" style={{ gap: 8 }}>
        {canEdit
          ? <Select aria-label={L("Model for ", "النموذج لـ ") + f.name} value={f.model} options={models} onChange={(e) => save({ model: e.target.value }, L("Model updated", "تم تحديث النموذج"))} data-testid={"ai-model-" + f.id} style={{ maxWidth: 220 }} />
          : <span className="xs num muted" dir="ltr">{f.model}</span>}
        <Button variant="sm" icon="sparkle" disabled={!f.enabled} onClick={onTry} data-testid={"ai-try-" + f.id}>{L("Try it", "جرّبه")}</Button>
      </div>
    </section>
  );
}

export default function AiPage() {
  const { L } = useI18n();
  const { role } = useSession();
  const ov = useOverlays();
  const q = useAiFeatures();
  const [tryId, setTryId] = useState<string | null>(null);
  const canEdit = role === "owner";
  return (
    <div data-testid="ai-page">
      <PageHeader title={L("AI automation", "الأتمتة الذكية")} sub={L("Turn documents, messages and spreadsheets into finished records. You always review before anything is saved.", "حوّل المستندات والرسائل والجداول إلى سجلات جاهزة، وتراجع دائمًا قبل الحفظ.")}
        actions={<Button variant="pri" icon="sparkle" onClick={() => ov.open({ kind: "assistant" })} data-testid="btn-assistant">{L("Ask the HR assistant", "اسأل المساعد")}</Button>} />
      <Loaded q={q}>{(cfg) => {
        const tasks = cfg.features.reduce((a, f) => a + f.uses, 0);
        const out = cfg.used >= cfg.limit;
        const open = cfg.features.find((f) => f.id === tryId);
        return (
          <>
            <div className="g g-4 mb">
              <Kpi label={L("Credits used (this month)", "الرصيد المستخدم (هذا الشهر)")} value={<span data-testid="ai-credits">{cfg.used} / {cfg.limit}</span>} icon="zap" foot={<Bar value={cfg.used} max={cfg.limit} tone={out ? "bad" : cfg.used / (cfg.limit || 1) > 0.8 ? "warn" : "gold"} />} />
              <Kpi label={L("Time saved this month (est.)", "الوقت الموفر (تقديري)")} value={<span data-testid="ai-saved">{timeSavedHours(cfg.features).toFixed(1)}</span>} unit={L("hours", "ساعة")} icon="clock" tone="info" foot={L("estimate: minutes saved per task × uses", "تقدير: الدقائق الموفرة × الاستخدام")} />
              <Kpi label={L("Tasks automated", "مهام مؤتمتة")} value={<span data-testid="ai-tasks">{tasks}</span>} icon="check" />
              <Kpi label={L("Data residency", "موقع البيانات")} value={cfg.residency ? L("Protected", "محمي") : L("Open", "مفتوح")} icon="shield" tone={cfg.residency ? "" : "warn"} foot={L("set by your StartHR administrator", "يضبطه مسؤول ستارت HR")} />
            </div>
            {out && <div className="mb"><Alert tone="bad" testId="ai-limit" title={L("AI credit limit reached", "تم بلوغ حد رصيد الذكاء")} desc={canEdit ? L("Add a +1,000 credits pack (SAR 99/month) when you renew in Billing. Credits reset at the start of each month.", "أضف باقة 1,000 رصيد (99 ر.س شهريًا) عند التجديد من الفوترة. يتجدد الرصيد أول كل شهر.") : L("Ask your company owner to add a credits pack. Credits reset at the start of each month.", "اطلب من مالك المنشأة إضافة باقة رصيد. يتجدد الرصيد أول كل شهر.")} /></div>}
            {!canEdit && <div className="mb"><Alert tone="info" tint title={L("Only the company owner can turn features on or off and choose models.", "يمكن لمالك المنشأة فقط تشغيل الميزات أو إيقافها واختيار النماذج.")} /></div>}
            <div className="g g-3">
              {cfg.features.map((f) => <FeatureCard key={f.id} f={f} cfg={cfg} canEdit={canEdit} onTry={() => setTryId(f.id)} />)}
            </div>
            {open && <AiTryModal f={open} residency={cfg.residency} onClose={() => setTryId(null)} />}
          </>
        );
      }}</Loaded>
    </div>
  );
}
